import axios, { HttpStatusCode } from 'axios';
import { sendNotification } from '@srm/ui/lib/notifications';
import { INotification } from '@srm/ui/notifications';
import $api from '@/http/index';
import { BackgroundJobKind, IBackgroundJob } from '@models/backgroundJobs/IBackgroundJob';
import { FilesService } from '@services/FilesService';
import { backgroundJobKind } from '@services/backgroundJobs/backgroundJobKinds';

const base = '/jobs';

const filePath = (jobId: string) => `jobs/${encodeURIComponent(jobId)}/file`;

/** Файл результата убран по сроку хранения: не сбой, а состояние — текстом вида, что делать, если он нужен. */
const fileGoneNotification = (kind?: BackgroundJobKind | null): INotification => ({
    title: 'Файл больше недоступен',
    message: backgroundJobKind(kind).fileExpiredText,
    variant: 'yellow',
});

/** Механизм выключен на сервере (`BackgroundJobs:Enabled=false`): контроллера нет, список отвечает 404. */
export class BackgroundJobsDisabledError extends Error {
    constructor() {
        super('Фоновые задачи выключены на сервере');
        this.name = 'BackgroundJobsDisabledError';
    }
}

const isNotFound = (error: unknown): boolean =>
    axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound;

/**
 * Опрос и отметки идут фоном, без участия пользователя: их отказ не повод для тоста и не ошибка страницы для
 * журнала поддержки. 404 разбирает вызывающий код — это «выключено» или «задачи уже нет», а не сбой.
 */
const BACKGROUND_REQUEST = {
    suppressErrorNotification: true,
    skipSupportErrorLog: true,
    passThroughStatuses: [HttpStatusCode.NotFound],
} as const;

/**
 * Старт фоновой задачи вида: отказы (400 — кнопке не разрешён фон, 403, 404 — механизм выключен, 413 — файл больше
 * предела, 429, 503) приходят исходной AxiosError без тоста — их показывает окно или таблица (`startBackgroundJob`,
 * `startGridExportJob`).
 */
export const BACKGROUND_START_REQUEST = {
    suppressErrorNotification: true,
    passThroughStatuses: [
        HttpStatusCode.BadRequest,
        HttpStatusCode.Forbidden,
        HttpStatusCode.NotFound,
        HttpStatusCode.PayloadTooLarge,
        HttpStatusCode.TooManyRequests,
        HttpStatusCode.ServiceUnavailable,
    ],
};

/** Фоновые задачи пользователя: раздел колокольчика и окно, которое ждёт итог. */
export class BackgroundJobsService {
    /**
     * Свои задачи, новые сверху: `active` — идущие и закончившиеся, но не увиденные; `limit` — сколько (сервер
     * ограничивает сверху). Выключенный механизм — {@link BackgroundJobsDisabledError}.
     */
    static async list(params: { active?: boolean; limit?: number } = {}): Promise<IBackgroundJob[]> {
        try {
            const response = await $api.get<IBackgroundJob[]>(base, {
                ...BACKGROUND_REQUEST,
                params: { active: params.active ?? true, limit: params.limit },
            });
            return response.data ?? [];
        } catch (error) {
            if (isNotFound(error)) throw new BackgroundJobsDisabledError();
            throw error;
        }
    }

    /** Состояние задачи (у задачи в очереди — с местом в очереди); `null` — задачи нет или она чужая. */
    static async get(jobId: string): Promise<IBackgroundJob | null> {
        try {
            const response = await $api.get<IBackgroundJob>(`${base}/${encodeURIComponent(jobId)}`, BACKGROUND_REQUEST);
            return response.data;
        } catch (error) {
            if (isNotFound(error)) return null;
            throw error;
        }
    }

    /**
     * Отменить. Во время фиксации отменить уже нельзя — ответ покажет текущее состояние; `null` — задачи нет. Отказ
     * показывает вызывающий — тост интерцептора видят не все.
     */
    static async cancel(jobId: string): Promise<IBackgroundJob | null> {
        try {
            const response = await $api.post<IBackgroundJob>(`${base}/${encodeURIComponent(jobId)}/cancel`, undefined, {
                suppressErrorNotification: true,
                passThroughStatuses: [HttpStatusCode.NotFound],
            });
            return response.data;
        } catch (error) {
            if (isNotFound(error)) return null;
            throw error;
        }
    }

    /** «Окно открыто и ждёт итог» — раз в 15 с из видимой вкладки: пока отметка свежая, колокольчик ждёт. */
    static async watch(jobId: string): Promise<void> {
        await $api.post(`${base}/${encodeURIComponent(jobId)}/watch`, undefined, BACKGROUND_REQUEST);
    }

    /** Итог показан пользователю (или файл скачан): уведомление о нём больше не нужно. */
    static async seen(jobId: string): Promise<void> {
        await $api.post(`${base}/${encodeURIComponent(jobId)}/seen`, undefined, BACKGROUND_REQUEST);
    }

    /**
     * Скачать файл результата (у отказа — файл с отметками ошибок) запросом в память вкладки. Файл живёт ограниченный
     * срок: после уборки — понятный тост вида задачи, а не «не удалось скачать».
     */
    static async downloadFile(jobId: string, kind?: BackgroundJobKind | null): Promise<void> {
        await FilesService.downloadFile(filePath(jobId), undefined, {
            notFoundNotification: fileGoneNotification(kind),
        });
    }

    /**
     * Скачать файл результата браузером — потоком на диск: архив в сотни МБ в память вкладки не ложится. Отказ
     * сервера браузер не объяснит — наличие файла проверяет вызывающий. `false` — без токена страницы, скачивание не
     * начато.
     */
    static downloadFileNative(jobId: string): boolean {
        return FilesService.tryDownloadFileNative(filePath(jobId));
    }

    /** Файла уже нет: тот же тост, что у 404 скачивания. */
    static notifyFileGone(kind?: BackgroundJobKind | null): void {
        sendNotification(fileGoneNotification(kind));
    }
}
